import { carrySettings } from 'further-ui/validations';
import { Control, Controller } from 'react-hook-form';
import { z } from 'zod';
import useCarriedInterestFeeTemplatesOptions from './hooks/useCarriedInterestFeeTemplatesOptions';
import { PreferredReturnAccrualMethod } from 'further-types/lp/carry-settings';
import { styled, TextField, Typography } from '@mui/material';
import { Card, CardContent } from 'further-ui/components/Card';
import { Column } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import BasicSelect from 'further-ui/components/BasicSelect';
import NumberInput from 'further-ui/components/NumberInput';
import CarriedInterestRate from './CarriedInterestRate';
import CarriedInterestRateRowLayout, {
  RateRowActionsSpacer,
} from './CarriedInterestRateRowLayout';
import useCarriedInterestFeeTemplate from './hooks/useCarriedInterestFeeTemplate';
import DraggableList from 'components/DraggableList';
import Switch from 'further-ui/components/Switch';
import PageSection from 'components/new/PageSection';
import Checkbox from 'further-ui/components/Checkbox';

const StyledColumn = styled(Column)(() => ({
  maxWidth: 700,
}));

const StyledDraggableList = styled(DraggableList)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const RateRowHeader = styled(CarriedInterestRateRowLayout)(({ theme }) => ({
  padding: theme.spacing(0, 2),
}));

type FormValues = z.infer<
  typeof carrySettings.CarriedInterestFeeTemplatesSettings
>;

type Props = {
  index: number;
  control: Control<FormValues>;
};

const CarriedInterestFeeTemplate: React.FC<Props> = ({ index, control }) => {
  const {
    handleChangePreferredReturnAccrualMethod,
    hasOtherDefaultTemplate,
    useCompoundAccrualMethod,
    useSimpleAccrualMethod,
    appendCarriedInterestRate,
    removeCarriedInterestRate,
    carriedInterestRateFields,
    handleCarriedInterestRatesReorder,
  } = useCarriedInterestFeeTemplate(index);

  const {
    defaultCarriedInterestRate,
    preferredReturnAccrualMethodOptions,
    preferredReturnAccrualPeriodOptions,
    preferredReturnCompoundingPeriodOptions,
    preferredReturnCalculationEndOptions,
    preferredReturnBasisOptions,
  } = useCarriedInterestFeeTemplatesOptions({ useSimpleAccrualMethod });

  const handleAddCarriedInterestRate = () => {
    appendCarriedInterestRate(defaultCarriedInterestRate);
  };

  const handleRemoveCarriedInterestRate = (index: number) => {
    if (carriedInterestRateFields.length === 1) return;

    removeCarriedInterestRate(index);
  };

  return (
    <StyledColumn spacing="lg">
      <FormField
        size="md"
        labelSize="md"
        breakpoint="md"
        layout="horizontal"
        label="Template name"
      >
        <Controller
          name={`carriedInterestFeeTemplates.${index}.name`}
          control={control}
          render={({ fieldState: { error }, field }) => (
            <TextField
              {...field}
              fullWidth
              placeholder="Enter name"
              error={!!error}
              helperText={error?.message}
              size="small"
            />
          )}
        />
      </FormField>
      <FormField
        size="md"
        labelSize="md"
        breakpoint="md"
        layout="horizontal"
        label="Default template"
      >
        <Controller
          name={`carriedInterestFeeTemplates.${index}.isDefault`}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Switch
              checked={!!value}
              onChange={(value) => onChange(value)}
              edge="start"
              disabled={hasOtherDefaultTemplate}
            />
          )}
        />
      </FormField>
      <FormField
        size="md"
        labelSize="md"
        breakpoint="md"
        layout="horizontal"
        label="Preferred return rate"
      >
        <Controller
          name={`carriedInterestFeeTemplates.${index}.preferredReturnRate`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, ...rest } }) => (
            <NumberInput
              {...rest}
              decimals={10}
              placeholder="Enter rate"
              fullWidth
              onChange={(value) => {
                onChange(value);
              }}
              error={!!error}
              helperText={error?.message}
              showPercentSuffix
            />
          )}
        />
      </FormField>
      <FormField
        size="md"
        labelSize="md"
        breakpoint="md"
        layout="horizontal"
        label="Preferred return accrual method"
      >
        <Controller
          name={`carriedInterestFeeTemplates.${index}.preferredReturnAccrualMethod`}
          control={control}
          render={({ fieldState: { error }, field: { value } }) => (
            <BasicSelect
              size="small"
              options={preferredReturnAccrualMethodOptions}
              placeholder="Select method"
              fullWidth
              error={!!error}
              helperText={error?.message}
              value={value}
              onChange={(val) =>
                handleChangePreferredReturnAccrualMethod(
                  val as PreferredReturnAccrualMethod,
                )
              }
            />
          )}
        />
      </FormField>
      <FormField
        size="md"
        labelSize="md"
        breakpoint="md"
        layout="horizontal"
        label="Preferred return accrual period"
        tooltip="This is the period after which preferred return is due. For example, a monthly accrual period means that each month the interest due increases, but in the middle of the month the interest due is the figure from the end of the past month."
      >
        <Controller
          name={`carriedInterestFeeTemplates.${index}.preferredReturnAccrualPeriod`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <BasicSelect
              size="small"
              options={preferredReturnAccrualPeriodOptions}
              placeholder="Select period"
              fullWidth
              error={!!error}
              helperText={error?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />
      </FormField>
      {useCompoundAccrualMethod && (
        <FormField
          size="md"
          breakpoint="md"
          labelSize="md"
          layout="horizontal"
          label="Preferred return compounding period"
          tooltip="This is the period after which preferred return will compound and be considered within the basis of future preferred return calculations. For example, a monthly compounding period means that at the end of each month preferred return interest is added to the principal for future interest calculations, but in the middle of the month the system will take the principal and interest sum from the end of the past month."
        >
          <Controller
            name={`carriedInterestFeeTemplates.${index}.preferredReturnCompoundingPeriod`}
            control={control}
            render={({ fieldState: { error }, field: { onChange, value } }) => (
              <BasicSelect
                size="small"
                placeholder="Select period"
                options={preferredReturnCompoundingPeriodOptions}
                fullWidth
                error={!!error}
                helperText={error?.message}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </FormField>
      )}
      <FormField
        size="md"
        labelSize="md"
        breakpoint="md"
        layout="horizontal"
        label="Preferred return calculation end"
      >
        <Controller
          name={`carriedInterestFeeTemplates.${index}.preferredReturnCalculationEnd`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <BasicSelect
              size="small"
              options={preferredReturnCalculationEndOptions}
              placeholder="Select calculation"
              fullWidth
              error={!!error}
              helperText={error?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />
      </FormField>
      <FormField
        size="md"
        breakpoint="md"
        labelSize="md"
        layout="horizontal"
        label="Preferred return basis"
      >
        <Controller
          name={`carriedInterestFeeTemplates.${index}.preferredReturnBasis`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <Column spacing="sm">
              <BasicSelect
                size="small"
                options={preferredReturnBasisOptions}
                placeholder="Select preferred return basis"
                fullWidth
                error={!!error}
                helperText={error?.message}
                value={value}
                onChange={onChange}
              />
              <Typography variant="body2">
                Preferred return is calculated from each capital call notice
                date.
              </Typography>
            </Column>
          )}
        />
      </FormField>
      <FormField
        size="md"
        breakpoint="md"
        labelSize="md"
        layout="horizontal"
        label="Carry partner catch-up speed"
        tooltip={
          <>
            This percentage determines how profits are allocated to the carried
            interest partner (CP) after the limited partners (LPs) receive their
            preferred return. For example:
            <br />- <b>100%:</b> The CP receives 100% of profits after the LPs'
            preferred return, until they reach their full carried interest
            entitlement, ensuring a rapid catch-up.
            <br /> - <b>50%:</b> The CP receives 50% of profits during the
            catch-up phase, with the other 50% going to general final profit
            split. This extends the time it takes the CP to reach its full
            carried interest entitlement. It does not typically reduce the total
            amount the CP receives if the fund performs well.
            <br />- Adjust this percentage to modify how quickly the CP catches
            up to its carried interest share before the final profit split
            occurs.
          </>
        }
      >
        <Controller
          name={`carriedInterestFeeTemplates.${index}.partnerCatchupSpeed`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, ...rest } }) => (
            <NumberInput
              {...rest}
              decimals={10}
              onChange={(value) => {
                onChange(value);
              }}
              placeholder="Enter percentage"
              fullWidth
              error={!!error}
              helperText={error?.message}
              showPercentSuffix
            />
          )}
        />
      </FormField>
      <Card variant="outlined">
        <CardContent size="sm">
          <PageSection
            withoutBottomDivider
            title={
              <Column spacing="sm">
                <Typography variant="inherit">
                  Carried interest rates
                </Typography>
                <Typography variant="body2">
                  This is the percentage of excess proceeds the carry partner is
                  entitled to receive. If you have rate tiers (based on return
                  thresholds), add each one and the relevant threshold (e.g. 20%
                  to 3x, 25% over). Larger rates override smaller rates from the
                  threshold point.
                </Typography>
              </Column>
            }
          >
            <Column spacing="xs">
              <RateRowHeader
                rate={
                  <Typography variant="h6" color="grey.500">
                    Rate
                  </Typography>
                }
                dpiThreshold={
                  <Typography variant="h6" color="grey.500">
                    DPI threshold
                  </Typography>
                }
                actions={<RateRowActionsSpacer />}
              />
              <StyledDraggableList
                onReorder={({ orderedItemIds }) =>
                  handleCarriedInterestRatesReorder(orderedItemIds)
                }
              >
                {carriedInterestRateFields.map((field, rateIndex) => (
                  <DraggableList.Item
                    key={field.id}
                    id={field.id}
                    withDragHandle
                  >
                    <CarriedInterestRate
                      draggable={carriedInterestRateFields.length > 1}
                      index={rateIndex}
                      templateIndex={index}
                      control={control}
                      onAddRate={handleAddCarriedInterestRate}
                      onRemoveRate={handleRemoveCarriedInterestRate}
                      showThreshold={rateIndex > 0}
                      allowDelete={carriedInterestRateFields.length > 1}
                    />
                  </DraggableList.Item>
                ))}
              </StyledDraggableList>
            </Column>

            {carriedInterestRateFields.length > 1 && (
              <Controller
                name={`carriedInterestFeeTemplates.${index}.carriedInterestPartnerCatchupBetweenRateThresholds`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    edge="start"
                    label={
                      <Typography variant="body2" color="grey.900">
                        Carried interest partner catch-up between rate
                        threshold(s)
                      </Typography>
                    }
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
            )}
          </PageSection>
        </CardContent>
      </Card>
    </StyledColumn>
  );
};

export default CarriedInterestFeeTemplate;
